import { ConversationHistory } from "../features/chat/ConversationHistory.tsx";
// Shared shell (RF-UI-03): left navigation, workspace selector, recent projects, user menu and a top bar with real
// health/stream state. On narrow screens the navigation lives in a modal drawer (RF-UI-14). D36: the navigation starts
// as an icon rail (tooltips, accessible names); it expands over the page and can be pinned open (then resizable,
// 220–280 px); the pin is remembered per browser. The side assistant does not exist on Home.
import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent, type ReactNode, type RefObject } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import type { ProjectSummary } from "@maestros/contracts";
import { useI18n, LOCALES } from "../i18n/index.tsx";
import type { MessageKey } from "../i18n/en.ts";
import { listProjects } from "../api/endpoints.ts";
import { useSession } from "../session/SessionProvider.tsx";
import { useRealtimeEvents } from "../realtime/RealtimeProvider.tsx";
import { readLocal, writeLocal } from "../lib/storage.ts";
import { useAsync, useDebouncedCallback } from "../lib/useAsync.ts";
import { Menu } from "../components/Menu.tsx";
import { isWithinOrOwnedBy } from "../components/floating.ts";
import { IconButton } from "../components/Button.tsx";
import { AGGREGATE_VIEW } from "../lib/status.ts";
import { StatusIcon } from "../components/Status.tsx";
import {
  ActivityIcon,
  BookIcon,
  BuildingIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  CpuIcon,
  FileTextIcon,
  FolderIcon,
  GlobeIcon,
  HomeIcon,
  InboxIcon,
  LogOutIcon,
  LogoIcon,
  MenuIcon,
  PanelRightIcon,
  PlayCircleIcon,
  PlugIcon,
  SettingsIcon,
  XIcon,
} from "../components/icons.tsx";
import { HealthIndicator, LiveIndicator } from "./ConnectionStatus.tsx";
import { AssistantProvider } from "../features/assistant/AssistantProvider.tsx";
import { AssistantButton, AssistantPanel, AssistantRail } from "../features/assistant/AssistantPanel.tsx";
import { PageTitleProvider, usePageTitleOverride } from "./PageTitle.tsx";

const MIN_W = 220;
const MAX_W = 280;
const DEFAULT_W = 232;
const PIN_KEY = "nav.pinned";
/** Rail items sit 8 px inside the 64 px rail: tooltips and menus open this far from them, clear of the rail's edge. */
const RAIL_GAP = 12;

/** "rail": icons only; "overlay": expanded over the page (not pinned); "pinned": expanded and part of the layout. */
export type NavMode = "rail" | "overlay" | "pinned";

interface NavEntry {
  to: string;
  label: MessageKey;
  icon: ReactNode;
  end?: boolean;
}

const PRIMARY_NAV: NavEntry[] = [
  { to: "/", label: "nav.home", icon: <HomeIcon />, end: true },
  { to: "/projects", label: "nav.projects", icon: <FolderIcon /> },
  { to: "/runs", label: "nav.runs", icon: <PlayCircleIcon /> },
  { to: "/prds", label: "nav.prds", icon: <FileTextIcon /> },
  { to: "/memory", label: "nav.memory", icon: <BookIcon /> },
  { to: "/settings", label: "nav.settings", icon: <SettingsIcon /> },
];

const SECONDARY_NAV: NavEntry[] = [
  { to: "/inbox", label: "nav.inbox", icon: <InboxIcon /> },
  { to: "/agents", label: "nav.agents", icon: <CpuIcon /> },
  { to: "/skills", label: "nav.skills" as any, icon: <BookIcon /> },
  { to: "/integrations", label: "nav.integrations", icon: <PlugIcon /> },
  { to: "/observability", label: "nav.observability", icon: <ActivityIcon /> },
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase() || "?";
}

function SectionTitle() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const override = usePageTitleOverride();
  const all = [...PRIMARY_NAV, ...SECONDARY_NAV];
  const match = all
    .filter((n) => (n.end ? pathname === n.to : pathname === n.to || pathname.startsWith(`${n.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (override) return <span className="topbar-title user-content" data-testid="topbar-title">{override}</span>;
  return <span className="topbar-title" data-testid="topbar-title">{match ? t(match.label) : ""}</span>;
}

function RecentProjects({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useI18n();
  const recent = useAsync((signal) => listProjects({ limit: 5 }, signal), []);
  const refresh = useDebouncedCallback(recent.reload, 1500);
  useRealtimeEvents((e) => {
    if (e.type.startsWith("project.")) refresh();
  });
  const items: ProjectSummary[] = recent.data?.items ?? [];
  return (
    <section className="nav-recent" aria-labelledby="nav-recent-title">
      <h2 id="nav-recent-title" className="nav-section-title">
        <ClockIcon size={16} />
        {t("nav.recent")}
      </h2>
      {recent.loading ? (
        <p className="nav-empty">{t("common.loading")}</p>
      ) : recent.error ? (
        <p className="nav-empty">{t("nav.recentUnavailable")}</p>
      ) : items.length === 0 ? (
        <p className="nav-empty">{t("nav.recentEmpty")}</p>
      ) : (
        <ul className="nav-list">
          {items.map((p) => (
            <li key={p.id}>
              <NavLink to={`/projects/${p.id}`} className="nav-link nav-link--recent" onClick={onNavigate}>
                <span className={`nav-recent-state tone-${AGGREGATE_VIEW[p.aggregate_state].tone}`} title={t(AGGREGATE_VIEW[p.aggregate_state].label)}>
                  <StatusIcon kind={AGGREGATE_VIEW[p.aggregate_state].icon} size={12} />
                </span>
                <span className="truncate">{p.name}</span>
                <span className="sr-only">— {t(AGGREGATE_VIEW[p.aggregate_state].label)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function NavItem({ entry, rail, small, onNavigate }: { entry: NavEntry; rail: boolean; small?: boolean; onNavigate?: (() => void) | undefined }) {
  const { t } = useI18n();
  const label = t(entry.label);
  return (
    <li>
      <NavLink to={entry.to} end={entry.end ?? false} className={`nav-link${small ? " nav-link--sm" : ""}`} onClick={onNavigate} {...(rail ? { "data-tip": label } : {})}>
        {entry.icon}
        <span className="nav-label">{label}</span>
      </NavLink>
    </li>
  );
}

interface NavControls {
  mode: NavMode;
  toggleId: string;
  sidebarId: string;
  onToggle: () => void;
  onPin: () => void;
}

function SidebarContent({ onNavigate, controls }: { onNavigate?: () => void; controls?: NavControls }) {
  const { t, locale, setLocale } = useI18n();
  const { me, signOut } = useSession();
  const orgName = me?.organization?.name || t("nav.brand");
  const displayName = me?.user?.display_name ?? "";
  const rail = controls?.mode === "rail";
  const expanded = controls ? controls.mode !== "rail" : true;
  return (
    <div className="sidebar-inner">
      <div className="brand">
        <LogoIcon size={34} className="brand-logo" />
        <div className="brand-text">
          <span className="brand-name">Primage</span>
          <span className="brand-sub">Studio</span>
        </div>
      </div>
      {controls ? (
        <div className="nav-controls">
          <button
            type="button"
            id={controls.toggleId}
            className="icon-btn nav-toggle"
            aria-label={t(expanded ? "nav.collapse" : "nav.expand")}
            aria-expanded={expanded}
            aria-controls={controls.sidebarId}
            onClick={controls.onToggle}
            {...(rail ? { "data-tip": t("nav.expand") } : {})}
          >
            <ChevronRightIcon size={18} className="nav-toggle-icon" />
          </button>
          {expanded ? (
            <button type="button" className="icon-btn nav-pin" aria-label={t("nav.pin")} title={t("nav.pin")} aria-pressed={controls.mode === "pinned"} onClick={controls.onPin}>
              <PanelRightIcon size={18} style={{ transform: "scaleX(-1)" }} />
            </button>
          ) : null}
        </div>
      ) : null}

      <Menu
        label={t("nav.workspace")}
        placement={rail ? "right-start" : "down-start"}
        gap={rail ? RAIL_GAP : 4}
        items={[
          { id: "l", kind: "label", label: t("nav.workspace") },
          { id: "current", label: orgName, icon: <CheckIcon size={16} />, checked: true, onSelect: () => undefined },
          { id: "sep", kind: "separator", label: "" },
          { id: "switch", label: t("nav.workspaceSwitch"), disabled: true, hint: t("common.laterPhase") },
        ]}
        trigger={(p) => (
          <button type="button" className="workspace-btn" {...p} aria-label={t("nav.workspaceSelector", { name: orgName })} {...(rail ? { "data-tip": orgName } : {})}>
            <span className="workspace-icon">
              <BuildingIcon size={16} />
            </span>
            <span className="stack sidebar-detail" style={{ gap: 0, minWidth: 0, flex: 1, textAlign: "left" }}>
              <span className="truncate workspace-name">{orgName}</span>
              <span className="truncate subtle" style={{ fontSize: "var(--text-xs)" }}>
                {me ? t(`role.${me.role}`) : ""}
              </span>
            </span>
            <ChevronDownIcon size={16} className="sidebar-detail" />
          </button>
        )}
      />

      <nav aria-label={t("nav.main")} className="nav">
        <ul className="nav-list">
          {PRIMARY_NAV.map((n) => (
            <NavItem key={n.to} entry={n} rail={rail} onNavigate={onNavigate} />
          ))}
        </ul>
        <div className="nav-divider" />
        <ul className="nav-list nav-list--secondary">
          {SECONDARY_NAV.map((n) => (
            <NavItem key={n.to} entry={n} rail={rail} small onNavigate={onNavigate} />
          ))}
        </ul>
        {rail ? null : (
          <>
            <div className="nav-divider" />
            <ConversationHistory compact {...(onNavigate ? { onNavigate } : {})} />
            <RecentProjects {...(onNavigate ? { onNavigate } : {})} />
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        <Menu
          label={t("user.menu")}
          placement={rail ? "right-end" : "up-start"}
          gap={rail ? RAIL_GAP : 4}
          items={[
            { id: "lang-label", kind: "label", label: t("user.language") },
            ...LOCALES.map((l) => ({
              id: `lang-${l.id}`,
              label: l.label,
              icon: <GlobeIcon size={16} />,
              checked: l.id === locale,
              onSelect: () => setLocale(l.id),
            })),
            { id: "sep", kind: "separator" as const, label: "" },
            { id: "signout", label: t("user.signOut"), icon: <LogOutIcon size={16} />, onSelect: () => void signOut() },
          ]}
          trigger={(p) => (
            <button type="button" className="user-btn" {...p} aria-label={t("user.menuFor", { name: displayName })} {...(rail ? { "data-tip": displayName } : {})}>
              <span className="avatar">{initials(displayName)}</span>
              <span className="stack sidebar-detail" style={{ gap: 0, minWidth: 0, flex: 1, textAlign: "left" }}>
                <span className="truncate" style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>
                  {displayName}
                </span>
                <span className="truncate subtle" style={{ fontSize: "var(--text-xs)" }}>
                  {me?.user?.email}
                </span>
              </span>
              <ChevronDownIcon size={16} className="sidebar-detail" />
            </button>
          )}
        />
      </div>
    </div>
  );
}

/** Tooltip of the icon rail: one fixed element next to the hovered or focused item (never clipped by the rail's scroll
 * area), with its own state so hovering never re-renders the page. Decorative for assistive technology — every item
 * already has its accessible name. Never shown for a trigger whose menu is open (it would cover the menu). */
function RailTooltip({ container, enabled }: { container: RefObject<HTMLElement | null>; enabled: boolean }) {
  const [tip, setTip] = useState<{ text: string; top: number; left: number } | null>(null);
  useEffect(() => {
    const root = container.current;
    if (!enabled || !root) {
      setTip(null);
      return;
    }
    const show = (e: Event) => {
      const el = e.target instanceof Element ? e.target.closest<HTMLElement>("[data-tip]") : null;
      if (!el || !root.contains(el)) return;
      if (el.getAttribute("aria-haspopup") && el.getAttribute("aria-expanded") === "true") {
        setTip(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setTip({ text: el.dataset["tip"] ?? "", top: r.top + r.height / 2, left: r.right + RAIL_GAP });
    };
    const hide = () => setTip(null);
    root.addEventListener("pointerover", show);
    root.addEventListener("focusin", show);
    root.addEventListener("pointerleave", hide);
    root.addEventListener("focusout", hide);
    root.addEventListener("click", hide);
    return () => {
      root.removeEventListener("pointerover", show);
      root.removeEventListener("focusin", show);
      root.removeEventListener("pointerleave", hide);
      root.removeEventListener("focusout", hide);
      root.removeEventListener("click", hide);
    };
  }, [container, enabled]);
  if (!tip) return null;
  return (
    <div className="rail-tooltip" aria-hidden="true" style={{ top: tip.top, left: tip.left }}>
      {tip.text}
    </div>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  // D36: on Home the page itself is the conversation, so the side assistant does not exist there.
  return (
    <AssistantProvider available={pathname !== "/" && !pathname.startsWith("/chats/")}>
      <PageTitleProvider>
        <ShellLayout />
      </PageTitleProvider>
    </AssistantProvider>
  );
}

function ShellLayout() {
  const { t } = useI18n();
  const [width, setWidth] = useState<number>(() => {
    const w = readLocal<number>("sidebarWidth", DEFAULT_W);
    return Number.isFinite(w) ? Math.min(MAX_W, Math.max(MIN_W, w)) : DEFAULT_W;
  });
  const [pinned, setPinned] = useState(() => readLocal<unknown>(PIN_KEY, false) === true);
  const [overlay, setOverlay] = useState(false);
  const mode: NavMode = pinned ? "pinned" : overlay ? "overlay" : "rail";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDialogElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const toggleId = useId();
  const sidebarId = useId();

  const commitWidth = useCallback((w: number) => {
    const clamped = Math.min(MAX_W, Math.max(MIN_W, Math.round(w)));
    setWidth(clamped);
    writeLocal("sidebarWidth", clamped);
  }, []);

  const savePinned = useCallback((next: boolean) => {
    setPinned(next);
    writeLocal(PIN_KEY, next);
  }, []);

  /** Collapses an overlay (never a pinned menu); with `refocus`, focus goes back to the expand toggle. */
  const collapseOverlay = useCallback((refocus: boolean) => {
    setOverlay(false);
    if (refocus) requestAnimationFrame(() => document.getElementById(toggleId)?.focus());
  }, [toggleId]);

  const onToggle = () => {
    if (mode === "rail") setOverlay(true);
    else {
      savePinned(false);
      setOverlay(false);
    }
  };
  // Unpinning keeps the menu open (as an overlay) so the pointer or focus is not left on a vanished control.
  const onPin = () => {
    if (pinned) {
      savePinned(false);
      setOverlay(true);
    } else {
      savePinned(true);
      setOverlay(false);
    }
  };

  // Width is applied directly (no transition), so dragging never animates layout.
  const onHandlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const move = (ev: globalThis.PointerEvent) => commitWidth(ev.clientX);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const onHandleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") commitWidth(width - 8);
    else if (e.key === "ArrowRight") commitWidth(width + 8);
    else if (e.key === "Home") commitWidth(MIN_W);
    else if (e.key === "End") commitWidth(MAX_W);
    else return;
    e.preventDefault();
  };

  // An overlay closes on Escape (focus back to the toggle), on a pointer press outside, when focus leaves it and on
  // navigation. Menus of the sidebar are portalled out of it (so the rail never clips them) but are owned by it, so
  // using them never closes the overlay.
  useEffect(() => {
    if (mode !== "overlay") return;
    const onPointer = (e: globalThis.PointerEvent) => {
      if (e.target instanceof Node && isWithinOrOwnedBy(sidebarRef.current, e.target)) return;
      collapseOverlay(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [mode, collapseOverlay]);

  const onSidebarKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape" && mode === "overlay" && !e.defaultPrevented) {
      e.preventDefault();
      collapseOverlay(true);
    }
  };
  const onSidebarBlur = (e: FocusEvent<HTMLElement>) => {
    if (mode === "overlay" && e.relatedTarget instanceof Node && !isWithinOrOwnedBy(e.currentTarget, e.relatedTarget)) collapseOverlay(false);
  };

  useEffect(() => {
    const d = drawerRef.current;
    if (!d) return;
    if (drawerOpen && !d.open) d.showModal();
    if (!drawerOpen && d.open) d.close();
  }, [drawerOpen]);

  useEffect(() => {
    setDrawerOpen(false);
    setOverlay(false);
  }, [location.pathname]);

  return (
    <div className="app-shell" data-nav={mode} style={{ ["--sidebar-width" as string]: `${width}px` }}>
      <a href="#main" className="skip-link">
        {t("common.skipToContent")}
      </a>
      <aside
        ref={sidebarRef}
        id={sidebarId}
        className={`sidebar sidebar--${mode}`}
        aria-label={t("nav.sidebar")}
        onKeyDown={onSidebarKeyDown}
        onBlur={onSidebarBlur}
      >
        <SidebarContent controls={{ mode, toggleId, sidebarId, onToggle, onPin }} {...(mode === "overlay" ? { onNavigate: () => collapseOverlay(false) } : {})} />
        {mode === "pinned" ? (
          <div
            className="sidebar-resizer"
            role="separator"
            tabIndex={0}
            aria-orientation="vertical"
            aria-label={t("nav.resize")}
            aria-valuemin={MIN_W}
            aria-valuemax={MAX_W}
            aria-valuenow={width}
            onPointerDown={onHandlePointerDown}
            onKeyDown={onHandleKey}
          />
        ) : null}
      </aside>
      <RailTooltip container={sidebarRef} enabled={mode === "rail"} />

      <dialog
        ref={drawerRef}
        className="drawer"
        aria-label={t("nav.sidebar")}
        onClose={() => setDrawerOpen(false)}
        onClick={(e) => {
          if (e.target === drawerRef.current) setDrawerOpen(false);
        }}
      >
        {drawerOpen ? (
          <div className="drawer-inner">
            <IconButton label={t("nav.closeMenu")} className="drawer-close" onClick={() => setDrawerOpen(false)}>
              <XIcon />
            </IconButton>
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </div>
        ) : null}
      </dialog>

      <div className="main-column">
        <header className="topbar">
          <IconButton label={t("nav.openMenu")} className="topbar-menu" onClick={() => setDrawerOpen(true)} aria-expanded={drawerOpen}>
            <MenuIcon />
          </IconButton>
          <SectionTitle />
          <AssistantButton />
          <div className="topbar-status" aria-label={t("conn.region")} role="group">
            <HealthIndicator />
            <span className="topbar-sep" aria-hidden="true" />
            <LiveIndicator />
          </div>
        </header>
        <main id="main" ref={mainRef} className="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <AssistantRail />
      <AssistantPanel />
    </div>
  );
}
